
import { Component, Prop, Provide, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Group, GroupUiClient, ListItem } from '../../backend/backendapi';
import { handleError, initClient } from '../../../api';
import { emptyUUID } from '../../../util';
import GroupStore from '../../../stores/GroupStore';
import TabButton from '../../util/TabButton';
import TabGeneral from './TabGeneral';
import TabUsers from './TabUsers';
import TabUsersExternal from './TabUsersExternal';
import TabSlot from './TabSlot';
import ExternalUserSelectModalTab from './ExternalUserSelectModal/ExternalUserSelectModalTab';
import { applyGroupSelector, GroupSelector } from '../util';

export type TabId = 'general' | 'users' | 'usersExternal' | string;

@Component({
  components: {
    TabButton,
    TabGeneral,
    TabSlot,
    TabUsers,
    TabUsersExternal,
    SelectModal: require('../../selectmodal/selectmodal.vue.html').default,
  },
})
export default class GroupEditor<T> extends Vue {
  @Prop({ required: true })
  readonly allUsers!: ListItem[];
  @Prop({ required: true })
  readonly availableSalutations!: string[];
  @Prop({ required: true })
  readonly group!: T;
  @Prop({ default: '' })
  readonly groupSelector!: GroupSelector<T>;
  @Prop({ default: () => (g: Group) => Group.fromJS(g) })
  readonly groupCopyFunction!: ((t: T) => T);
  @Prop({ default: null })
  readonly groupSaveFunction!: ((t: T) => Promise<T>) | null;
  @Prop({ default: () => [] })
  readonly hiddenTabs!: TabId[];
  @Prop({ default: () => new Map() })
  readonly tabTitles!: Map<TabId, string>;
  @Prop({ default: false })
  readonly hidePinned!: boolean;
  @Prop({ default: false })
  readonly hideExternalUserDisabled!: boolean;
  @Prop({ default: false })
  readonly hideMassSelection!: boolean;
  @Prop({ default: false })
  readonly allowNoEmailForExternalUsers!: boolean;

  readonly defaultTabTitles = new Map<TabId, string>([
    ['general', 'group.editModal.tabs.general'],
    ['users', 'group.editModal.tabs.users'],
    ['usersExternal', 'group.editModal.tabs.usersExternal'],
  ]);
  saving = false;
  tab: string = '';
  slottedTabs: TabSlot[] = [];

  //
  private readonly store = new GroupStore();

  //api
  private readonly api = initClient(GroupUiClient);

    get availableSalutationsIncludingEditingGroup(): string[] {
    const defaultSalutations = ['Herr', 'Frau'];

    return [
      ...new Set(
          [
            ...defaultSalutations, // Add defaults first
            ...this.availableSalutations,
            ...(this.extractedGroup?.externalUsers ?? []) //add salutations of this group since this group may be newly created
                .map(e => e.salutation)
                .filter(e => e?.trim().length)
                .map(e => e!),
          ],
      ),
    ];
  }

  get enabledSlottedTabs(): TabSlot[] {
    const ret = this.slottedTabs.filter(tab => !this.hiddenTabs.includes(tab.id));
    ret.sort((a, b) => a.order - b.order);
    return ret;
  }

  get extractedGroup(): Group {
    return applyGroupSelector(this.group, this.groupSelector);
  }

  getTableTitle(id: TabId): string {
    if (this.tabTitles.has(id)) {
      return this.$t(this.tabTitles.get(id)!).toString();
    }
    if (this.defaultTabTitles.has(id)) {
      return this.$t(this.defaultTabTitles.get(id)!).toString();
    }
    return id;
  }

  private async groupSaveFunctionInternal(): Promise<Group> {
    let res;
    let added = !this.extractedGroup.id || this.extractedGroup.id === emptyUUID();
    if (added) {
      res = await this.api.create(this.extractedGroup);
    } else {
      res = await this.api.update(this.extractedGroup);
    }
    this.store.rememberGroup(res);
    return res;
  }

  //events
  async onSave(): Promise<T | undefined> {
    this.saving = true;
    try {
      let res: T;
      if (this.groupSaveFunction) {
        res = await this.groupSaveFunction(this.group);
      } else {
        res = (await this.groupSaveFunctionInternal()) as T;
      }
      await this.triggerOnSaveOnSlottedTabs();
      this.$toasted.info(this.$t('txtSaved').toString());
      return res;
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
      return undefined;
    } finally {
      this.saving = false;
    }
  }

  async triggerOnSaveOnSlottedTabs(): Promise<void> {
    for (let slottedTab of this.slottedTabs) {
      await slottedTab.onSave();
    }
  }

  @Provide()
  addTab(tab: ExternalUserSelectModalTab) {
    this.slottedTabs.push(tab);
  }

  @Provide()
  removeTab(tab: ExternalUserSelectModalTab) {
    const index = this.slottedTabs.findIndex(t => t === tab);
    if (index >= 0) {
      this.slottedTabs.splice(index, 1);
    }
  }

  @Watch('tab')
  @Watch('enabledSlottedTabs')
  private tabChanged(): void {
    //select first tab if none selected
    if (!this.tab && this.enabledSlottedTabs.length > 0) {
      this.tab = this.enabledSlottedTabs[0].id;
    }

    //update tab visibility
    this.enabledSlottedTabs.forEach(tab => {
      const newVisible = tab.id === this.tab;
      if (tab.visible !== newVisible) { //only set when different, prevent endless event looping
        tab.visible = newVisible;
      }
    });
  }
}
